/**
 * Resale Marketplace Utilities
 * 
 * This module provides client-side functions for the secondary marketplace
 * with proper listing state management and real-time updates.
 */

import { supabase } from '../lib/supabase.js';
import { listPieceWithDelegateAuth, cancelListingWithRevoke, verifyDelegateBeforePurchase } from './delegatedMarketplace.ts';
import { requestDelegateTransfer } from './delegatedMarketplace.ts';
import { executeSaleTransaction, calculateSaleFee } from './marketplaceTransactions';
import { getConnection } from '../config/rpc';
import { trackTransaction } from './performanceMonitor';

const LAMPORTS_PER_SOL = 1_000_000_000;

/**
 * Create a new listing (resale)
 * @param {Object} wallet - Connected wallet adapter
 * @param {Object} piece - Piece to list
 * @param {number} priceSol - Listing price in SOL
 * @param {Object} options - Optional parameters
 * @param {string} options.cluster - Override cluster for this listing (after switch)
 * @returns {Promise<Object>} Listing result
 */
export async function createListing(wallet, piece, priceSol, options = {}) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  console.log('📝 Creating listing for:', piece.name);
  console.log('Price:', priceSol, 'SOL', options.cluster ? `on ${options.cluster}` : '');

  // Determine mint address (might be in piece.mint_address or piece.id for external NFTs)
  const mintAddress = piece.mint_address || (typeof piece.id === 'string' && piece.id.length > 32 ? piece.id : null);
  
  if (!mintAddress) {
    throw new Error('Piece must have a valid mint address before listing');
  }

  // Validate price
  if (!priceSol || priceSol <= 0) {
    throw new Error('Price must be greater than 0');
  }

  try {
    const connection = getConnection({ commitment: 'confirmed', cluster: options.cluster });

    // Step 1: Create/refresh delegate authorization
    console.log('🔐 Setting up delegate authorization...');
    const delegateResult = await listPieceWithDelegateAuth({
      wallet,
      piece: { ...piece, mint_address: mintAddress }, // Ensure mint_address is set
      priceSol,
      connection
    });

    if (!delegateResult.success) {
      throw new Error('Failed to set up delegate authorization');
    }

    console.log('✅ Delegate authorization set:', delegateResult.authSignature);

    // Step 2: Call edge function to create listing
    // For external NFTs, pieceId might be a mint address string - let edge function handle it
    console.log('📤 Creating listing record...');
    const requestBody = {
      sellerWallet: wallet.publicKey.toString(),
      mintAddress: mintAddress,
      priceSol: priceSol,
      delegateSignature: delegateResult.authSignature,
    };
    
    // Include cluster if provided (after switch)
    if (options.cluster) {
      requestBody.cluster = options.cluster;
    }
    
    // Only include pieceId if it's a valid numeric ID
    if (typeof piece.id === 'number' || (typeof piece.id === 'string' && /^\d+$/.test(piece.id))) {
      requestBody.pieceId = piece.id;
    }
    
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-listing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to create listing');
    }

    console.log('✅ [LIST ACTIVITY] Listing created:', result.listing?.id);
    console.log('✅ [LIST ACTIVITY] Mint address:', result.listing?.mint_address);
    console.log('✅ [LIST ACTIVITY] Price:', result.listing?.price_sol, 'SOL');
    
    // FALLBACK: Directly insert list activity (same as mint tracking logic)
    // This ensures activity is tracked even if edge function fails
    try {
      console.log('📝 [LIST ACTIVITY] Creating nft_activity entry directly (frontend fallback)...');
      
      // Check if list activity already exists to prevent duplicates
      const { data: existingListActivity, error: checkError } = await supabase
        .from('nft_activity')
        .select('id')
        .eq('mint_address', result.listing.mint_address)
        .eq('event_type', 'list')
        .eq('from_address', result.listing.seller_wallet)
        .gte('created_at', new Date(Date.now() - 60000).toISOString()) // Last 60 seconds
        .maybeSingle();

      if (checkError) {
        console.warn('⚠️ [LIST ACTIVITY] Error checking for existing activity:', checkError.message);
      }

      if (existingListActivity) {
        console.log('ℹ️ [LIST ACTIVITY] List activity already exists, skipping duplicate');
      } else {
        const now = new Date();
        const timestamp = Math.floor(now.getTime() / 1000); // Unix timestamp in seconds
        const priceLamports = Math.floor(result.listing.price_sol * 1e9);
        const tempSignature = 'app-list-' + result.listing.mint_address + '-' + timestamp; // Create placeholder signature
        
        const activityData = {
          mint_address: result.listing.mint_address,
          event_type: 'list',
          from_address: result.listing.seller_wallet, // Who listed it
          to_address: null, // No recipient for listing
          price: result.listing.price_sol, // Price in SOL
          amount_lamports: priceLamports,
          event_timestamp: timestamp, // Unix timestamp in seconds
          block_time: now.toISOString(),
          created_at: now.toISOString(),
          slot: 0,
          signature: tempSignature, // Use placeholder since listing has no on-chain tx
          tx_sig: tempSignature,
          source: 'app'
        };

        console.log('📝 [LIST ACTIVITY] Inserting activity data:', activityData);

        const { data: insertedActivity, error: activityError } = await supabase
          .from('nft_activity')
          .insert(activityData)
          .select();

        if (activityError) {
          console.error('❌ [LIST ACTIVITY] Failed to create nft_activity entry:', activityError.message);
          console.error('❌ [LIST ACTIVITY] Error details:', activityError);
          console.error('❌ [LIST ACTIVITY] Activity data attempted:', activityData);
        } else {
          console.log('✅ [LIST ACTIVITY] nft_activity entry created successfully!');
          console.log('✅ [LIST ACTIVITY] Inserted activity:', insertedActivity);
        }
      }
    } catch (activityError) {
      console.error('❌ [LIST ACTIVITY] Activity logging failed (non-critical):', activityError);
    }

    return {
      success: true,
      listing: result.listing,
      updatedPiece: result.updatedPiece, // Edge function returns this
      message: result.message,
    };
  } catch (error) {
    console.error('❌ Create listing failed:', error);
    
    // Pass through WRONG_CLUSTER errors unchanged (for UI to handle)
    if (error.code === 'WRONG_CLUSTER') {
      throw error;
    }
    
    // Map error messages to user-friendly versions
    if (error.message) {
      // Cluster mismatch
      if (error.message.includes('Mint not found on current network') || 
          error.message.includes('Mint not found on this network') || 
          error.message.includes('Mint not found on this cluster')) {
        throw new Error('Mint not found on current network. Switch to the correct cluster and try again.');
      }
      
      // Ownership error
      if (error.message.includes('Only the current on-chain owner')) {
        throw new Error('Only the current on-chain owner can list this item.');
      }
      
      // Programmable NFT
      if (error.message.includes('Programmable NFT')) {
        throw new Error('Programmable NFTs are not supported yet.');
      }
      
      // Program detection failure
      if (error.message.includes('Could not detect token program')) {
        const mintOwner = error.message.match(/Mint owner: ([A-Za-z0-9]+)/)?.[1];
        const message = mintOwner 
          ? `Could not detect token program. Mint owner: ${mintOwner}. This token may not be compatible.`
          : 'Could not detect token program. This token may not be compatible with our marketplace.';
        throw new Error(message);
      }
      
      // Legacy token (info, not error)
      if (error.message.includes('legacy') || error.message.includes('NonFungibleLegacy')) {
        console.warn('⚠️ No metadata found; proceeding as legacy 1/1 SPL token.');
      }
      
      // SFT (info, not error)
      if (error.message.includes('SFT') || error.message.includes('Semi-fungible')) {
        console.warn('⚠️ Semi-fungible token detected. Listing quantity=1.');
      }
    }
    
    throw error;
  }
}

/**
 * Cancel an active listing
 * @param {Object} wallet - Connected wallet adapter
 * @param {Object} listing - Listing to cancel
 * @param {Object} piece - Associated piece
 * @returns {Promise<Object>} Cancellation result
 */
export async function cancelListing(wallet, listing, piece) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  console.log('❌ Canceling listing:', listing.id);

  try {
    const connection = getConnection({ commitment: 'confirmed' });

    // Step 1: Revoke delegate on-chain (optional but recommended)
    let revokeSignature = null;
    try {
      console.log('🔐 Revoking delegate authorization...');
      const revokeResult = await cancelListingWithRevoke({
        wallet,
        piece,
        connection
      });
      revokeSignature = revokeResult.signature;
      console.log('✅ Delegate revoked:', revokeSignature);
    } catch (revokeError) {
      console.warn('⚠️ Delegate revocation failed (will cancel listing anyway):', revokeError);
      // Continue with cancellation even if revoke fails
    }

    // Step 2: Call edge function to cancel listing
    console.log('📤 Canceling listing in database...');
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-listing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        listingId: listing.id,
        sellerWallet: wallet.publicKey.toString(),
        revokeSignature: revokeSignature,
        reason: 'seller_canceled',
      }),
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to cancel listing');
    }

    console.log('✅ Listing canceled');

    return {
      success: true,
      message: result.message,
      listing: result.listing,
    };
  } catch (error) {
    console.error('❌ Cancel listing failed:', error);
    throw error;
  }
}

/**
 * Purchase a listed piece (secondary sale)
 * @param {Object} wallet - Connected wallet adapter
 * @param {Object} piece - Piece to purchase
 * @param {Object} listing - Active listing
 * @param {number} price - Purchase price in SOL
 * @returns {Promise<Object>} Purchase result
 */
export async function purchaseListing(wallet, piece, listing, price) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  // Track performance
  const startTime = Date.now();

  console.log('🛒 Purchasing listing:', listing.id);
  console.log('Piece:', piece.name);
  console.log('Price:', price, 'SOL');
  console.log('Seller:', listing.seller_wallet);

  try {
    // Validate buyer isn't seller
    if (listing.seller_wallet === wallet.publicKey.toString()) {
      throw new Error('You cannot buy your own listing');
    }

    // Validate piece is minted
    if (!piece.mint_address) {
      throw new Error('Piece is not minted and cannot be purchased');
    }

    const connection = getConnection({ commitment: 'confirmed' });
    const PLATFORM_WALLET_ADDRESS = '8cm597KjGmwTSzadrcVCHVpqwzdRZy1BM2yUrDDEpJcB';

    // Step 1: Verify delegate authorization is active (pre-purchase check)
    console.log('🔍 Verifying delegate authorization...');
    const delegateCheck = await verifyDelegateBeforePurchase({
      connection,
      mintAddress: piece.mint_address,
      sellerPubkeyBase58: listing.seller_wallet,
      platformDelegateBase58: PLATFORM_WALLET_ADDRESS,
    });

    if (!delegateCheck.ok) {
      throw new Error(
        delegateCheck.error || 
        'Delegate authorization is not active. The listing may have expired or been canceled.'
      );
    }

    console.log('✅ Delegate authorization verified');

    // Step 2: Get creator info for royalty payment
    console.log('💰 Fetching creator and royalty info...');
    const creatorWallet = piece.creator_wallet || piece.creators?.wallet;
    const royaltyBps = piece.royalty_bps || 0;
    
    if (royaltyBps > 0 && creatorWallet) {
      console.log(`   Creator: ${creatorWallet}`);
      console.log(`   Royalty: ${royaltyBps} bps (${(royaltyBps / 100).toFixed(1)}%)`);
    }

    // Step 3: Execute SOL payment (including royalties)
    console.log('💰 Processing payment...');
    const paymentResult = await executeSaleTransaction(
      wallet, 
      listing.seller_wallet, 
      price,
      creatorWallet,
      royaltyBps
    );
    console.log('✅ Payment successful:', paymentResult.signature);
    console.log('   Explorer:', paymentResult.explorerUrl);

    // Step 4: Request server-side NFT transfer
    console.log('🎨 Requesting NFT transfer via delegate...');
    const transferResult = await requestDelegateTransfer({
      piece,
      sellerWallet: listing.seller_wallet,
      buyerWallet: wallet.publicKey.toString(),
      paymentSignature: paymentResult.signature,
      priceLamports: Math.floor(price * LAMPORTS_PER_SOL),
      wallet: wallet // Buyer wallet for signing gas fee transaction
    });

    console.log('✅ NFT transfer completed:', transferResult.transferSignature);
    console.log('   Explorer:', transferResult.explorerUrl);

    // Calculate fee breakdown including royalties
    const feeBreakdown = calculateSaleFee(price, null, royaltyBps);

    // Track success
    trackTransaction('purchase', startTime, true);

    // Return complete purchase details
    return {
      success: true,
      paymentTransaction: {
        signature: paymentResult.signature,
        explorerUrl: paymentResult.explorerUrl,
      },
      nftTransaction: {
        signature: transferResult.transferSignature,
        explorerUrl: transferResult.explorerUrl,
      },
      feeBreakdown,
      listing: {
        id: listing.id,
        status: 'filled',
      },
    };
  } catch (error) {
    console.error('❌ Purchase failed:', error);
    
    // Track failure
    trackTransaction('purchase', startTime, false, error.message);
    
    throw error;
  }
}

/**
 * Get active listing for a mint address
 * @param {string} mintAddress - Mint address to check
 * @returns {Promise<Object|null>} Active listing or null
 */
export async function getActiveListing(mintAddress) {
  try {
    const { data, error } = await supabase
      .from('listings')
      .select(`
        *,
        pieces!inner(
          id,
          name,
          description,
          category,
          thumbnail_url,
          preview_glb_url,
          model_url,
          royalty_bps,
          creators!inner(
            id,
            wallet,
            handle,
            is_verified,
            profile_picture_url
          )
        )
      `)
      .eq('mint_address', mintAddress)
      .eq('status', 'active')
      .maybeSingle();

    if (error) {
      console.error('Error fetching active listing:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching active listing:', error);
    return null;
  }
}

/**
 * Get listing history for a mint address
 * @param {string} mintAddress - Mint address
 * @returns {Promise<Array>} Listing history
 */
export async function getListingHistory(mintAddress) {
  try {
    const { data, error } = await supabase
      .from('listings')
      .select('id, seller_wallet, buyer_wallet, price_sol, status, created_at, filled_at, canceled_at')
      .eq('mint_address', mintAddress)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching listing history:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching listing history:', error);
    return [];
  }
}

/**
 * Subscribe to listing changes for real-time updates
 * @param {string} mintAddress - Mint address to watch
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Subscription object
 */
export function subscribeToListing(mintAddress, callback) {
  console.log('👁️ Subscribing to listing updates for:', mintAddress);

  const subscription = supabase
    .channel(`listing:${mintAddress}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'listings',
        filter: `mint_address=eq.${mintAddress}`,
      },
      (payload) => {
        console.log('📡 Listing update:', payload);
        callback(payload);
      }
    )
    .subscribe();

  return subscription;
}

/**
 * Subscribe to piece changes for real-time updates
 * @param {string} mintAddress - Mint address to watch
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Subscription object
 */
export function subscribeToPiece(mintAddress, callback) {
  console.log('👁️ Subscribing to piece updates for:', mintAddress);

  const subscription = supabase
    .channel(`piece:${mintAddress}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'pieces',
        filter: `mint_address=eq.${mintAddress}`,
      },
      (payload) => {
        console.log('📡 Piece update:', payload);
        callback(payload);
      }
    )
    .subscribe();

  return subscription;
}

/**
 * Check if current wallet can list a piece
 * @param {Object} wallet - Connected wallet
 * @param {Object} piece - Piece to check
 * @returns {Object} Eligibility result
 */
export function canListPiece(wallet, piece) {
  if (!wallet.connected || !wallet.publicKey) {
    return {
      canList: false,
      reason: 'Wallet not connected',
    };
  }

  if (!piece.mint_address) {
    return {
      canList: false,
      reason: 'Piece is not minted',
    };
  }

  const walletAddress = wallet.publicKey.toString();
  const holderAddress = piece.holder_wallet;

  if (holderAddress !== walletAddress) {
    return {
      canList: false,
      reason: 'Only the current owner can list this item',
    };
  }

  // Remove listing_status check - backend handles all listing conflicts
  // The backend will:
  // 1. Verify caller is the owner
  // 2. Cancel ALL active listings (including stale/delegate)
  // 3. Create new listing
  // So we don't need to block here based on piece.listing_status
  
  // Allow relisting even if piece was previously sold or listed
  // The holder_wallet check above ensures only current owner can list
  return {
    canList: true,
  };
}

/**
 * Check if current wallet can purchase a listing
 * @param {Object} wallet - Connected wallet
 * @param {Object} listing - Listing to check
 * @param {Object} piece - Associated piece
 * @returns {Object} Eligibility result
 */
export function canPurchaseListing(wallet, listing, piece) {
  if (!wallet.connected || !wallet.publicKey) {
    return {
      canPurchase: false,
      reason: 'Wallet not connected',
    };
  }

  if (!listing || listing.status !== 'active') {
    return {
      canPurchase: false,
      reason: 'Listing is not active',
    };
  }

  if (piece.listing_status !== 'listed') {
    return {
      canPurchase: false,
      reason: 'Piece is not listed for sale',
    };
  }

  const walletAddress = wallet.publicKey.toString();
  const sellerAddress = listing.seller_wallet;

  if (walletAddress === sellerAddress) {
    return {
      canPurchase: false,
      reason: 'You cannot buy your own listing',
    };
  }

  return {
    canPurchase: true,
  };
}

/**
 * Reconcile external transfer for a mint address
 * @param {string} mintAddress - Mint address to reconcile
 * @param {string} signature - Optional transaction signature
 * @returns {Promise<Object>} Reconciliation result
 */
export async function reconcileExternalTransfer(mintAddress, signature = null) {
  try {
    console.log('🔄 Reconciling external transfer for:', mintAddress);

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reconcile-transfer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        mintAddress,
        signature,
      }),
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to reconcile transfer');
    }

    console.log('✅ Reconciliation complete:', result);

    return result;
  } catch (error) {
    console.error('❌ Reconciliation failed:', error);
    throw error;
  }
}

